import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, map, startWith } from 'rxjs';
import { Constants } from 'src/app/config/app.constants';

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  private smSubject = new BehaviorSubject<boolean>(false);
  private mdSubject = new BehaviorSubject<boolean>(false);
  private desktopBaseDown = new BehaviorSubject<boolean>(false);
  private desktopUp = new BehaviorSubject<boolean>(false);

  sm$ = this.smSubject.asObservable();
  md$ = this.mdSubject.asObservable();
  desktopBaseDown$ = this.desktopBaseDown.asObservable();
  desktopUp$ = this.desktopUp.asObservable();
  
  constructor(@Inject(PLATFORM_ID) private platformId: Object, private constants: Constants, @Inject(DOCUMENT) private dom: Document) {
    if (isPlatformBrowser(this.platformId)) {
      this.sm$ = this.media(`(max-width: 767px)`);
      this.md$ = this.media(`(min-width: 768px) and (max-width: 1023px)`);
      this.desktopBaseDown$ = this.media(`(max-width: 1023px)`);
      this.desktopUp$ = this.media(`(min-width: 1200px)`);
    }
  }

  media(query: string): Observable<boolean> {
    const mediaQuery = window.matchMedia(query);
    return fromEvent<MediaQueryList>(mediaQuery, 'change').pipe(
      startWith(mediaQuery),
      map((list: MediaQueryList) => list.matches)
    );
  }

  isPlatformBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
  * Method to validate whether a string contains harmful content 
  * @param value string to be sanitized
  * @returns 
  */
  sanitize(value: string):string {
    value = decodeURIComponent(value)
    if(value.match(/( or | and |\$|\[|\]|\r\n|\n|\r|\*|\^|<.*?[a-zA-Z0-9].*\/?>)/ig)) {
      return '';
    }
    return value;
  }

  /**
   * To sanitize query parameters
   * @param queryParams 
   * @returns 
   */
  sanitizeParams(queryParams: any) {
    if (Object.keys(queryParams).length === 0)
      return queryParams
    const sanitizedParams: any = {};
    for (const key in queryParams) {
      const sKey = this.sanitize(key);
      const sValue = this.sanitize(queryParams[key])
      if(sKey.length > 0 && sValue.length > 0)
        sanitizedParams[sKey] = sValue;
    }
    return sanitizedParams;
  }

  /**
   * Removes the black listed keys from the query params object
   * @param queryParams 
   * @returns 
   */
  removeBlackListedParams(queryParams: any) {
    if(typeof queryParams === 'object') {
      this.constants.blackListParams.forEach(item => {
        delete queryParams[item]
      })
    }
    return queryParams;
  }

  formatUrl(url: string): string {
      const [urlWithoutFragment, urlFragment] = url.split('#');
      const urlParts = urlWithoutFragment.split('?');
     
      if (urlParts[0].startsWith('http') || urlParts[0].endsWith('/')) {
        return url;
      }
  
      const queryStringParams = urlParts[1] ? `?${urlParts[1]}` : '';
      const fragment = urlFragment ? `#${urlFragment}` : '';
      return `${urlParts[0]}/${queryStringParams}${fragment}`;
    }

  a11yClick(event: Event): boolean {
    if (event instanceof MouseEvent) {
      return event.type === 'click';
    } else if (event instanceof KeyboardEvent) {
      return event.key === 'Enter' || event.key === ' ';
    }
    return false;
  }

  updateLinkTabIndex(index: number, selector: string, timeout = 0) {
    if (!this.isPlatformBrowser()) return;
    setTimeout(() => {
      this.dom.querySelectorAll(selector).forEach((link: HTMLAnchorElement) => {
          link.setAttribute('tabindex', index.toString());
      });
    }, timeout);
  }

  getTrackByUniqueId(): string {
    // Generate a unique id using index, title, and a GUID
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

}
